import { Inject, Injectable, InjectionToken, NgZone, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

/** The media query of the OS / browser "reduce motion" setting. */
export const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

/** A change of the media query (the part of `MediaQueryListEvent` the service reads). */
export interface MotionMediaQueryEvent {
    readonly matches: boolean;
}

/** A change listener of the media query. */
export type MotionMediaQueryListener = (event: MotionMediaQueryEvent) => void;

/**
 * The part of `MediaQueryList` the service reads. `addEventListener` is the current API; `addListener` is the older one
 * (Safari before 14, some embedded webviews), used only when the current one is missing.
 */
export interface MotionMediaQueryList {
    readonly matches: boolean;
    addEventListener?(type: 'change', listener: MotionMediaQueryListener): void;
    removeEventListener?(type: 'change', listener: MotionMediaQueryListener): void;
    addListener?(listener: MotionMediaQueryListener): void;
    removeListener?(listener: MotionMediaQueryListener): void;
}

/** `window.matchMedia`, or a stand-in (specs). */
export type MotionMatchMedia = (query: string) => MotionMediaQueryList | null | undefined;

/** The browser's `matchMedia`, or null where there is none (no window, a very old engine). */
export function browserMatchMedia(): MotionMatchMedia | null {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
        return null;
    }
    return (query: string): MotionMediaQueryList => window.matchMedia(query);
}

/** The `matchMedia` the service reads. Root default: the browser's; a spec provides a stand-in. */
export const MOTION_MATCH_MEDIA = new InjectionToken<MotionMatchMedia | null>('MOTION_MATCH_MEDIA', {
    providedIn: 'root',
    factory: browserMatchMedia,
});

/**
 * `PrefersReducedMotionService` — whether the user asked the system for reduced motion, live (P0-10; epic §11.2, §37).
 * Shared by the onboarding shell and field, the dark sign-in (D-01), the handoff (B-11) and the Intelligence page (F),
 * so every animated surface follows the same setting: when it is on, nothing drifts, flies or crossfades, and a sequence
 * becomes an instant switch (OB-S-04, OB-E-09, OB-H-04).
 *
 * CSS should still carry its own `@media (prefers-reduced-motion: reduce)` rule (the `reduced-motion` mixin of
 * `_dark-entry-tokens.scss`); this service is for what CSS cannot see: timers, script-driven sequences and the classes a
 * component sets. Without `matchMedia`, or when it throws, motion counts as allowed (`false`) and nothing breaks.
 *
 * providedIn 'root'; no `DataService` channel. The listener is removed when the root injector is destroyed.
 */
@Injectable({ providedIn: 'root' })
export class PrefersReducedMotionService implements OnDestroy {
    private readonly state: BehaviorSubject<boolean>;
    private readonly query: MotionMediaQueryList | null;
    private readonly listener: MotionMediaQueryListener;
    private listening: 'event' | 'legacy' | null = null;
    private destroyed = false;

    /** Whether reduced motion is asked for: the current value first, then every change. */
    readonly reduced$: Observable<boolean>;

    constructor(@Inject(MOTION_MATCH_MEDIA) matchMedia: MotionMatchMedia | null, private readonly zone: NgZone) {
        this.query = readQuery(matchMedia);
        this.state = new BehaviorSubject<boolean>(!!this.query && !!this.query.matches);
        this.reduced$ = this.state.pipe(distinctUntilChanged());
        this.listener = (event: MotionMediaQueryEvent): void => this.onChange(event);
        this.listen();
    }

    /** Whether reduced motion is asked for now. */
    get reduced(): boolean {
        return this.state.value;
    }

    /**
     * The duration to use for an animation or a timed step: `ms` normally, 0 under reduced motion (an instant switch).
     * A negative or missing value is 0.
     */
    duration(ms: number): number {
        const value: number = typeof ms === 'number' && isFinite(ms) && ms > 0 ? ms : 0;

        return this.state.value ? 0 : value;
    }

    ngOnDestroy(): void {
        this.destroyed = true;
        this.unlisten();
        this.state.complete();
    }

    private onChange(event: MotionMediaQueryEvent): void {
        const matches = !!event && !!event.matches;

        if (this.destroyed) {
            return;
        }
        this.zone.run(() => this.state.next(matches));
    }

    private listen(): void {
        const query: MotionMediaQueryList | null = this.query;

        if (!query) {
            return;
        }
        try {
            if (typeof query.addEventListener === 'function') {
                query.addEventListener('change', this.listener);
                this.listening = 'event';
            } else if (typeof query.addListener === 'function') {
                query.addListener(this.listener);
                this.listening = 'legacy';
            }
        } catch {
            // The engine refused the listener; the first value still stands.
            this.listening = null;
        }
    }

    private unlisten(): void {
        const query: MotionMediaQueryList | null = this.query;
        const listening: 'event' | 'legacy' | null = this.listening;

        this.listening = null;
        if (!query || !listening) {
            return;
        }
        try {
            if (listening === 'event' && typeof query.removeEventListener === 'function') {
                query.removeEventListener('change', this.listener);
            } else if (listening === 'legacy' && typeof query.removeListener === 'function') {
                query.removeListener(this.listener);
            }
        } catch {
            // Nothing to clean up that could still fire.
        }
    }
}

/** The media query list, or null when there is no `matchMedia` or it throws. */
function readQuery(matchMedia: MotionMatchMedia | null): MotionMediaQueryList | null {
    if (typeof matchMedia !== 'function') {
        return null;
    }
    try {
        return matchMedia(REDUCED_MOTION_QUERY) || null;
    } catch {
        return null;
    }
}
